import { z } from "zod";

import { CREDENTIAL_TYPE, isPlausibleSetupKey, MFA_METHOD } from "../common";

/// Coerces the specified array to a gauranteed non-empty type, or throws if the array
/// is in fact empty.
function expect_not_empty<T>(items: T[]): [T, ...T[]] {
  if (items.length === 0) {
    throw new Error("Expected non-empty array");
  }
  return items as [T, ...T[]];
}

export const CREDENTIAL_USER_TYPE = {
  AGENT: "AGENT",
  HUMAN: "HUMAN",
} as const;

export type CredentialUserType =
  (typeof CREDENTIAL_USER_TYPE)[keyof typeof CREDENTIAL_USER_TYPE];

export const USER_ID_OPTIONS: Record<CredentialUserType, string[]> = {
  AGENT: ["CAMI", "BENNY", "MONET"],
  HUMAN: ["OPS", "SELF"],
};

export const USER_ID_DEFAULT: Record<CredentialUserType, string> = {
  AGENT: "CAMI",
  HUMAN: "OPS",
};

const payerCredentialsFormSchemaBase = z.object({
  type: z.enum(expect_not_empty(Object.values(CREDENTIAL_TYPE))),
  userType: z
    .enum(expect_not_empty(Object.values(CREDENTIAL_USER_TYPE)))
    .default(CREDENTIAL_USER_TYPE.AGENT),
  userId: z.string().optional().default("CAMI"),
  username: z.string().min(1, "A username is required"),
  notes: z.string().min(1, "If provided, notes must not be empty").optional(),
  website: z.string().url().min(1, "Website must not be empty").optional(),
  password: z.string().min(1, "A password is required"),
  source: z.string().min(1, "A source must be selected").optional(),
  unsupportedPayer: z.string().min(1, "A source must be entered").optional(),
  pms: z.string().min(1, "A pms must be selected").optional(),
  practiceId: z
    .string({
      required_error: "A practice must be selected",
    })
    .min(1, "A practice must be selected"),
  activationCode: z
    .string()
    .length(24, "Activation code must be exactly 24 characters")
    .optional(),
  serialNumber: z
    .string()
    .length(24, "Serial Number must be exactly 24 characters")
    .optional(),
  customerNumber: z
    .string()
    .min(4, "Customer Number must be at least 4 characters")
    .optional(),
  machineIdentifier: z
    .string()
    .length(36, "Machine ID r must be exactly 36 characters")
    .optional(),
  mfaJson: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val.trim() === "") return true;
        try {
          JSON.parse(val);
          return true;
        } catch {
          return false;
        }
      },
      { message: "MFA JSON must be valid JSON" },
    ),
  mfaMethod: z
    .enum([MFA_METHOD.NONE, MFA_METHOD.EMAIL, MFA_METHOD.AUTHENTICATOR])
    .default(MFA_METHOD.NONE),
  mfaEmail: z
    .string()
    .email("Must be a valid email address")
    .optional()
    .or(z.literal("")),
  mfaSetupKey: z.string().optional(),
});

// Method-dependent MFA validation shared by the add and update forms. Only the
// field relevant to the selected method is required; the others are ignored.
function refineMfa(
  data: { mfaMethod?: string; mfaEmail?: string; mfaSetupKey?: string },
  ctx: z.RefinementCtx,
) {
  if (data.mfaMethod === MFA_METHOD.EMAIL) {
    const email = data.mfaEmail?.trim();
    if (!email) {
      ctx.addIssue({
        path: ["mfaEmail"],
        code: z.ZodIssueCode.custom,
        message: "An MFA email is required for the Email method",
      });
    }
  } else if (data.mfaMethod === MFA_METHOD.AUTHENTICATOR) {
    if (!isPlausibleSetupKey(data.mfaSetupKey)) {
      ctx.addIssue({
        path: ["mfaSetupKey"],
        code: z.ZodIssueCode.custom,
        message: "Enter a valid authenticator setup key (base32)",
      });
    }
  }
}

export const updatePayerCredentialsFormSchemaBase =
  payerCredentialsFormSchemaBase
    .pick({
      username: true,
      password: true,
      website: true,
      notes: true,
      mfaJson: true,
      mfaMethod: true,
      mfaEmail: true,
      mfaSetupKey: true,
      activationCode: true,
      serialNumber: true,
      customerNumber: true,
      machineIdentifier: true,
      userType: true,
      userId: true,
    })
    .partial()
    .superRefine(refineMfa);

export const addPayerCredentialsFormSchemaBase =
  payerCredentialsFormSchemaBase.superRefine((data, ctx) => {
    refineMfa(data, ctx);
    if (data.type === CREDENTIAL_TYPE.PAYER && !data.source) {
      ctx.addIssue({
        path: ["source"],
        code: z.ZodIssueCode.custom,
        message: "Source is required when type is PAYER",
      });
    }
    if (data.type === CREDENTIAL_TYPE.PMS && !data.pms) {
      ctx.addIssue({
        path: ["pms"],
        code: z.ZodIssueCode.custom,
        message: "PMS is required when type is PMS",
      });
    }
    if (
      data.type === CREDENTIAL_TYPE.PMS &&
      (!data.website || data.website.trim() === "")
    ) {
      ctx.addIssue({
        path: ["website"],
        code: z.ZodIssueCode.custom,
        message: "Website is required when PMS is selected",
      });
    }
    if (
      data.type === CREDENTIAL_TYPE.PMS &&
      data.pms &&
      data.pms === "ORTHO2_EDGE"
    ) {
      if (!data.activationCode || data.activationCode.trim() === "") {
        ctx.addIssue({
          path: ["activationCode"],
          code: z.ZodIssueCode.custom,
          message: "Activation code is required for Ortho2 PMS",
        });
      }
      if (!data.serialNumber || data.serialNumber.trim() === "") {
        ctx.addIssue({
          path: ["serialNumber"],
          code: z.ZodIssueCode.custom,
          message: "Serial number is required for Ortho2 PMS",
        });
      }
      if (!data.customerNumber || data.customerNumber.trim() === "") {
        ctx.addIssue({
          path: ["customerNumber"],
          code: z.ZodIssueCode.custom,
          message: "Customer number is required for Ortho2 PMS",
        });
      }
      if (!data.machineIdentifier || data.machineIdentifier.trim() === "") {
        ctx.addIssue({
          path: ["machineIdentifier"],
          code: z.ZodIssueCode.custom,
          message: "Machine ID is required for Ortho2 PMS",
        });
      }
    }
  });

export type AddPayerCredentialsFormFields = z.infer<
  typeof payerCredentialsFormSchemaBase
>;

export type UpdatePayerCredentialsFormFields = z.infer<
  typeof updatePayerCredentialsFormSchemaBase
>;
